import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AD_PERFORMANCE_REPORT,
  REPORT_OPTIONS,
  AD_REPORT_EXPORT,
} from "../../constants/api-constants";
import ApiServices from "../../services/ApiServices";
import AdPerformanceReport from "../AdManagement/AdPerformanceReport/AdPerformanceReport";
import {
  AD_PERFORMANCE_REPORT_NAME,
  isAdminReportRole,
  isDistributorReportRole,
  mapApiAdToRow,
} from "../AdManagement/AdPerformanceReport/adPerformanceReportData";
import {
  applyTableFilters,
  getAdReportColumns,
} from "../AdManagement/AdPerformanceReport/adPerformanceReportUtils";
import Reports from "./Reports";
import "./Reports.css";

const AD_PERFORMANCE_REPORT_OPTION = {
  name: AD_PERFORMANCE_REPORT_NAME,
  value: AD_PERFORMANCE_REPORT_NAME,
};

const defaultDateFrom = moment().startOf("month");
const defaultDateTo = moment();

export function getExportFileName(contentDisposition = "") {
  if (!contentDisposition || typeof contentDisposition !== "string") {
    return "ad-performance-report.csv";
  }
  const utf8NameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return decodeURIComponent(
    (
      utf8NameMatch?.[1] ||
      asciiNameMatch?.[1] ||
      "ad-performance-report.csv"
    ).trim(),
  );
}

export function downloadBlob(blob, fileName) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function getExportErrorMessage(response) {
  try {
    const errorJson = await response.json();
    return (
      errorJson?.responseStatus?.error ||
      errorJson?.message ||
      "Failed to export report."
    );
  } catch (_) {
    return "Failed to export report.";
  }
}

const ReportPage = () => {
  const history = useHistory();
  const location = useLocation();
  const [reportOptions, setReportOptions] = useState([]);
  const [selectedReport, setSelectedReport] = useState("");
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [searchToken, setSearchToken] = useState(0);
  const [dateFromErrorMessage, setDateFromErrorMessage] = useState("");
  const [dateToErrorMessage, setDateToErrorMessage] = useState("");
  const [adRows, setAdRows] = useState([]);
  const [adSummary, setAdSummary] = useState(null);
  const [adPagination, setAdPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 10,
  });
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortColumn, setSortColumn] = useState("adId");
  const [sortType, setSortType] = useState("DESC");
  const [searchedDateFrom, setSearchedDateFrom] = useState(null);
  const [searchedDateTo, setSearchedDateTo] = useState(null);
  const [hasSearchedAdReport, setHasSearchedAdReport] = useState(false);
  const [isReportOptionsLoading, setIsReportOptionsLoading] = useState(false);
  const [isReportSearchLoading, setIsReportSearchLoading] = useState(false);

  const isAdPerformanceRoute = location.pathname.includes("ad-performance");
  const isInitialAdPerformanceRoute = useRef(isAdPerformanceRoute);
  const roleId = Number(localStorage.getItem("selectedRole") || 1);
  const isDistributorRole = isDistributorReportRole(roleId);
  const canViewAdPerformanceReport =
    isDistributorRole || isAdminReportRole(roleId);
  const adColumns = getAdReportColumns(roleId);
  const filteredAdRows = applyTableFilters(adRows, []);
  const isAdPerformanceSelected =
    selectedReport === AD_PERFORMANCE_REPORT_OPTION.value;
  const selectedReportValue = reportOptions.some(
    (option) => option.value === selectedReport,
  )
    ? selectedReport
    : "";

  const calculateMaxDate = () => {
    if (dateFrom) {
      const oneMonthFromStartDate = moment(dateFrom).add(1, "months");
      return oneMonthFromStartDate.isBefore(moment())
        ? oneMonthFromStartDate
        : moment();
    }
    return moment();
  };

  const maxDate = calculateMaxDate();
  const isDateToGreaterThanToday = dateTo && moment(dateTo).isAfter(moment());
  const isDateToBeforeDateFrom =
    dateTo && moment(dateTo).isBefore(moment(dateFrom));
  const isSearchDisabled =
    isReportOptionsLoading ||
    isReportSearchLoading ||
    !dateFrom?._isValid ||
    !dateTo?._isValid ||
    isDateToGreaterThanToday ||
    isDateToBeforeDateFrom ||
    !dateFrom ||
    !dateTo ||
    !selectedReport;

  useEffect(() => {
    const loadReportOptions = async () => {
      setIsReportOptionsLoading(true);
      try {
        if (isDistributorRole) {
          setReportOptions([AD_PERFORMANCE_REPORT_OPTION]);
          setSelectedReport(AD_PERFORMANCE_REPORT_OPTION.value);
          return;
        }

        const response = await ApiServices.httpPost(REPORT_OPTIONS, {});
        if (response.responseStatus.isSuccess) {
          const options = Array.isArray(response.result) ? response.result : [];
          const optionsWithoutAdPerformance = options.filter(
            (option) => option.value !== AD_PERFORMANCE_REPORT_OPTION.value,
          );
          const nextOptions = canViewAdPerformanceReport
            ? [...optionsWithoutAdPerformance, AD_PERFORMANCE_REPORT_OPTION]
            : optionsWithoutAdPerformance;

          setReportOptions(nextOptions);
          if (!isInitialAdPerformanceRoute.current && nextOptions.length > 0) {
            setSelectedReport(
              (currentReport) => currentReport || nextOptions[0].value,
            );
          }
        }
      } catch (e) {
        toast.error("Internal server error");
      } finally {
        setIsReportOptionsLoading(false);
      }
    };

    loadReportOptions();
  }, [canViewAdPerformanceReport, isDistributorRole]);

  useEffect(() => {
    if (isAdPerformanceRoute && canViewAdPerformanceReport) {
      setSelectedReport(AD_PERFORMANCE_REPORT_OPTION.value);
    }
  }, [canViewAdPerformanceReport, isAdPerformanceRoute]);

  const handleReportChange = (event) => {
    const reportValue = event.target.value;
    setSelectedReport(reportValue);
    setAdRows([]);
    setAdSummary(null);
    setAdPagination({
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
      pageSize,
    });
    setHasSearchedAdReport(false);
    setSearchedDateFrom(null);
    setSearchedDateTo(null);
    history.push(
      reportValue === AD_PERFORMANCE_REPORT_OPTION.value
        ? "/Reports/ad-performance"
        : "/Reports",
    );
  };

  const handleDateFromChange = (newDateFrom) => {
    const today = moment();
    setDateFrom(newDateFrom);
    if (
      moment(newDateFrom, "MM/DD/YYYY", true).isValid() &&
      moment(newDateFrom).isAfter(today)
    ) {
      setDateFromErrorMessage("'Date From' must be today or a past date.");
    } else {
      setDateFromErrorMessage("");
    }
    if (dateTo && moment(newDateFrom).isAfter(dateTo)) {
      setDateToErrorMessage("");
      setDateTo(null);
    }
    if (dateTo && moment(newDateFrom).add(1, "months").isBefore(dateTo)) {
      setDateFromErrorMessage("");
      setDateToErrorMessage("");
      setDateTo(null);
    }
  };

  const handleDateToChange = (newDateTo) => {
    setDateTo(newDateTo);
    if (
      dateFrom &&
      (moment(newDateTo).subtract(1, "months").isAfter(dateFrom) ||
        moment(newDateTo).isBefore(dateFrom))
    ) {
      setDateToErrorMessage(
        "Please select a 'Date From' within one month of the 'Date To'.",
      );
    } else {
      const today = moment();
      if (
        moment(newDateTo, "MM/DD/YYYY", true).isValid() &&
        moment(newDateTo).isAfter(today)
      ) {
        setDateToErrorMessage("'Date To' must be today or a past date.");
      } else {
        setDateToErrorMessage("");
      }
    }
  };

  const handleSearch = () => {
    if (isAdPerformanceSelected) {
      // Reset to first page whenever a fresh search is run.
      setPageNumber(1);
      searchAdPerformanceReport({ pageNumber: 1 });
      return;
    }
    setIsReportSearchLoading(true);
    setSearchToken((currentToken) => currentToken + 1);
  };

  const searchAdPerformanceReport = async (overrides = {}) => {
    setIsReportSearchLoading(true);
    // Snapshot the date range at the moment Search is clicked so the charts
    // and KPI cards only reflect the searched range, not subsequent picker
    // changes the user makes before clicking Search again.
    const searchDateFrom = overrides.dateFrom ?? dateFrom;
    const searchDateTo = overrides.dateTo ?? dateTo;
    setSearchedDateFrom(searchDateFrom);
    setSearchedDateTo(searchDateTo);

    const effectivePageNumber = overrides?.pageNumber ?? pageNumber;
    const effectivePageSize = overrides?.pageSize ?? pageSize;
    const effectiveSortColumn = overrides?.sortColumn ?? sortColumn;
    const effectiveSortType = overrides?.sortType ?? sortType;

    try {
      const payload = {
        isShowChildUserData: true,
        searchText: "",
        selectedIds: [],
        pageSize: effectivePageSize,
        pageNumber: effectivePageNumber,
        sortColumn: effectiveSortColumn,
        sortType: effectiveSortType,
        filter: {
          dateFrom: moment(searchDateFrom).format("YYYY-MM-DD"),
          dateTo: moment(searchDateTo).format("YYYY-MM-DD"),
          distributorIds: [],
        },
        isSearchInAllQuery: true,
      };

      const response = await ApiServices.httpPost(
        AD_PERFORMANCE_REPORT,
        payload,
      );

      if (response?.responseStatus?.isSuccess && response?.result) {
        const result = response.result;
        const records = Array.isArray(result.records) ? result.records : [];
        setAdRows(records.map(mapApiAdToRow));
        setAdSummary(result.summary ?? null);
        setAdPagination({
          totalRecords: result.totalRecords ?? records.length,
          totalPages: result.totalPages ?? 0,
          currentPage: result.currentPage ?? effectivePageNumber,
          pageSize: result.pageSize ?? effectivePageSize,
        });
      } else {
        setAdRows([]);
        setAdSummary(null);
        setAdPagination({
          totalRecords: 0,
          totalPages: 0,
          currentPage: effectivePageNumber,
          pageSize: effectivePageSize,
        });
        toast.error(
          response?.responseStatus?.error || "Failed to load report.",
        );
      }
    } catch (e) {
      toast.error("Internal server error");
      setAdRows([]);
      setAdSummary(null);
    } finally {
      setHasSearchedAdReport(true);
      setIsReportSearchLoading(false);
    }
  };

  const exportAdPerformanceReport = async () => {
    try {
      const payload = {
        isShowChildUserData: true,
        searchText: "",
        selectedIds: [],
        pageSize: adPagination.pageSize,
        pageNumber: adPagination.currentPage,
        sortColumn,
        sortType,
        filter: {
          dateFrom: moment(searchedDateFrom).format("YYYY-MM-DD"),
          dateTo: moment(searchedDateTo).format("YYYY-MM-DD"),
          distributorIds: [],
        },
        isSearchInAllQuery: true,
      };

      const response = await ApiServices.httpPostRaw(AD_REPORT_EXPORT, payload);

      if (!response.ok) {
        const serverMessage = await getExportErrorMessage(response);
        toast.error(serverMessage);
        return;
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        toast.error("Failed to export report.");
        return;
      }

      const fileName = getExportFileName(
        response.headers.get("content-disposition"),
      );

      downloadBlob(blob, fileName);
      toast.success("Report exported successfully.");
    } catch (err) {
      toast.error(err?.message || "Failed to export report.");
    }
  };

  const renderDateControls = () => (
    <>
      <Grid item xs={12} sm={6} md={2}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DesktopDatePicker
            label={
              <span>
                Date From{" "}
                <span style={{ color: "red", marginLeft: "3px" }}>*</span>
              </span>
            }
            value={dateFrom}
            onChange={handleDateFromChange}
            maxDate={moment()}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  style: { fontWeight: "500", color: "black" },
                }}
                inputProps={{
                  ...params.inputProps,
                  placeholder: "MM/DD/YYYY",
                }}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DesktopDatePicker
            label={
              <span>
                Date To{" "}
                <span style={{ color: "red", marginLeft: "3px" }}>*</span>
              </span>
            }
            value={dateTo}
            onChange={handleDateToChange}
            minDate={dateFrom}
            maxDate={maxDate}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  style: { fontWeight: "500", color: "black" },
                }}
                inputProps={{
                  ...params.inputProps,
                  placeholder: "MM/DD/YYYY",
                }}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSearch}
          disabled={isSearchDisabled}
          endIcon={
            (isReportOptionsLoading || isReportSearchLoading) && (
              <CircularProgress size={16} color="inherit" />
            )
          }
          style={{ height: "38px", marginTop: "-2px" }}
        >
          Search
        </Button>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        {dateFromErrorMessage && (
          <Typography className="reports-error-message" color="error">
            {dateFromErrorMessage}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        {dateToErrorMessage && (
          <Typography className="reports-error-message" color="error">
            {dateToErrorMessage}
          </Typography>
        )}
      </Grid>
    </>
  );

  const renderAdPerformanceActions = () => (
    <>
      <Grid item xs={12} sm={6} md display="flex" justifyContent="flex-end">
        <div className="add-welcome-widgets">
          <Tooltip
            placement="bottom"
            title={
              filteredAdRows.length > 0 ? "Export to CSV" : "No data to export"
            }
          >
            <IosShareRoundedIcon
              data-testid="IosShareRoundedIcon"
              onClick={
                filteredAdRows.length > 0
                  ? exportAdPerformanceReport
                  : undefined
              }
              style={{
                cursor: filteredAdRows.length > 0 ? "pointer" : "not-allowed",
                background: filteredAdRows.length > 0 ? "#1aaefa" : "lightgray",
              }}
            />
          </Tooltip>
        </div>
      </Grid>
    </>
  );

  const renderReportControls = () => (
    <Grid
      container
      item
      xs={12}
      spacing={2}
      alignItems="center"
      className="report-page-controls"
      pl={4}
      pr={4}
      ml={2}
    >
      <Grid item xs={12} sm={6} md={3}>
        <Select
          labelId="report-select-label"
          fullWidth
          value={selectedReportValue}
          onChange={handleReportChange}
          displayEmpty
          style={{ height: "39px" }}
        >
          <MenuItem value="" disabled>
            Choose a report
          </MenuItem>
          {reportOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      {renderDateControls()}
      {isAdPerformanceSelected && renderAdPerformanceActions()}
      <Grid item xs={12}>
        <Typography className="reports-message-typography ">
          Please note that reports can only be generated for a maximum period of
          one month. Select a 'Date To' that is within one month of the 'Date
          From'.
        </Typography>
      </Grid>
    </Grid>
  );

  const renderSelectedReport = () => {
    switch (selectedReport) {
      case AD_PERFORMANCE_REPORT_OPTION.value:
        return (
          <AdPerformanceReport
            columns={adColumns}
            rows={filteredAdRows}
            hasSearched={hasSearchedAdReport}
            dateFrom={searchedDateFrom}
            dateTo={searchedDateTo}
            summary={adSummary}
            pagination={adPagination}
            sortColumn={sortColumn}
            sortType={sortType}
            onPageChange={(nextPage) => {
              setPageNumber(nextPage);
              searchAdPerformanceReport({ pageNumber: nextPage });
            }}
            onPageSizeChange={(nextSize) => {
              setPageSize(nextSize);
              setPageNumber(1);
              searchAdPerformanceReport({ pageNumber: 1, pageSize: nextSize });
            }}
            onSortChange={(column, type) => {
              setSortColumn(column);
              setSortType(type);
              searchAdPerformanceReport({
                sortColumn: column,
                sortType: type,
              });
            }}
          />
        );
      default:
        return (
          <Reports
            selectedReport={selectedReport}
            dateFrom={dateFrom}
            dateTo={dateTo}
            searchToken={searchToken}
            onReportLoadingChange={setIsReportSearchLoading}
          />
        );
    }
  };

  return (
    <Grid spacing={2} container className="reports-container">
      <Grid container item xs={12}>
        <Grid item xs={10}>
          <Box pt={2} pr={4} pl={4} pb={0}>
            <h5>Reports</h5>
          </Box>
        </Grid>
      </Grid>
      {renderReportControls()}
      <Grid item xs={12}>
        {renderSelectedReport()}
      </Grid>
    </Grid>
  );
};

export default ReportPage;
